import React, {useState, useEffect, useMemo} from 'react';
import { inject } from 'mobx-react';
import {withI18n} from 'spa/Components/Context';
import PropTypes from 'prop-types';

import DocumentTitle from 'spa/Components/DocumentTitle';
import DocumentMeta from 'spa/Components/DocumentMeta';

import Divider from '@urbanmassage/component-library/lib/Divider/Divider';
import GiftCard from './GiftCard';
import GiftCardBuyStepOne from './GiftCardBuyStepOne';
import GiftCardBuyStepTwo from './GiftCardBuyStepTwo';
import GiftsFooter from './GiftsFooter';
import GiftsNavigation from './GiftsNavigation';

import './buy-gift-card.less';
import GiftCardBuyStepThree from './GiftCardBuyStepThree';

const GiftCardBuy = ({
  billing, productsV2, session, tracker,
}, { i18n }) => {
  // check if is browser and not server
  if (typeof window === 'undefined') {
    return null;
  }

  const recipientInitial = {
    address: '',
    city: '',
    email: '',
    from: '',
    name: '',
    postcode: '',
    sendDay: '',
    shippingOption: 'now',
  };

  const [currencySymbol, setCurrencySymbol] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).currencySymbol : null); // eslint-disable-line
  const [currentStep, setCurrentStep] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).currentStep : 1); // eslint-disable-line
  const [customAmount, setCustomAmount] = useState(null);
  const [defaultTreatment, setDefaultTreatment] = useState(null); // eslint-disable-line
  const [deliveryOption, setDeliveryOption] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).deliveryOption : {label: '', value: '', price: 0}); // eslint-disable-line
  const [giftAmount, setGiftAmount] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).giftAmount : null); // eslint-disable-line
  const [giftMessage, setGiftMessage] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).giftMessage : i18n._x('Hope you enjoy this gift!', 'Gifts page')); // eslint-disable-line
  const [giftTreatment, setGiftTreatment] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).giftTreatment : null); // eslint-disable-line
  const [giftType, setGiftType] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).giftType : 'treatment'); // eslint-disable-line
  const [selectedCity, setSelectedCity] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).selectedCity : null); // eslint-disable-line
  const [recipient, setRecipient] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).recipient : recipientInitial); // eslint-disable-line
  const [giftUpgrade, setGiftUpgrade] = useState(sessionStorage.getItem('giftsV2') ? JSON.parse(sessionStorage.getItem('giftsV2')).giftUpgrade : undefined); // eslint-disable-line
  const [giftTreatmentInvalid, setGiftTreatmentInvalid] = useState(false); // eslint-disable-line
  const [giftAmountInvalid, setGiftAmountInvalid] = useState(false); // eslint-disable-line
  const [giftAmountCustomInvalid, setGiftAmountCustomInvalid] = useState(false); // eslint-disable-line
  const [giftUpgradeInvalid, setGiftUpgradeInvalid] = useState(false); // eslint-disable-line
  const [hasGiftUpgradeOptions, setHasGiftUpgradeOptions] = useState(false); // eslint-disable-line
  const [sendDateInvalid, setSendDateInvalid] = useState(false); // eslint-disable-line
  const [showFooter, setShowFooter] = useState(true);

  const loadFromParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlCity = urlParams.get('city-id');
    const urlGiftTreatment = urlParams.get('treatment-voucher-option-id');

    if (urlCity && urlGiftTreatment) {
      setSelectedCity(urlCity);
      setDefaultTreatment(urlGiftTreatment);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const voucherAmount = useMemo(() => {
    if (giftType === 'treatment') {
      return (giftTreatment && giftTreatment.value) ? giftTreatment.value : null;
    }
    if (giftType === 'amount') {
      if (giftAmount === 'custom') {
        return customAmount;
      }
      return giftAmount;
    }
    return null;
  }, [giftType, giftTreatment, giftAmount, customAmount]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentStep === 1) {
      if (giftType === 'treatment' && !giftTreatment) {
        setGiftTreatmentInvalid(true);
        return;
      }
      if (giftType === 'amount' && !giftAmount) {
        setGiftAmountInvalid(true);
        return;
      }
      if (giftType === 'amount' && giftAmount === 'custom' && (customAmount < 10 || !customAmount)) {
        setGiftAmountCustomInvalid(true);
        return;
      }
      if (!giftUpgrade && giftUpgrade !== null && hasGiftUpgradeOptions && giftType === 'treatment') {
        setGiftUpgradeInvalid(true);
        tracker.track('Error - Gift Duration Upsell Not Selected', {
          category: 'Gifts',
        });

        return;
      }
      setCurrentStep(2);
    }
    if (currentStep === 2) {
      if (recipient.shippingOption === 'later' && !recipient.sendDay) {
        setSendDateInvalid(true);
        return;
      }
      setCurrentStep(3);
    }
  };

  const handleSetCustomAmount = (val) => {
    setCustomAmount(val);
    setGiftAmountCustomInvalid(false);
  };

  const handleSelectDeliveryOption = (val) => {
    setDeliveryOption(val);
  };

  const handleSelectCity = (val) => {
    setGiftAmount(null);
    setGiftTreatment(null);
    setCustomAmount(null);
    setSelectedCity(val);
  };

  const handleSelectGiftType = (val) => {
    if (val === 'treatment') {
      setGiftAmount(null);
      setCustomAmount(null);
    }
    if (val === 'amount') {
      setGiftTreatment(null);
      setDefaultTreatment(null);
      setGiftUpgrade(null);
    }
    setGiftAmountInvalid(false);
    setGiftAmountCustomInvalid(false);
    setGiftTreatmentInvalid(false);
    setGiftUpgradeInvalid(false);
    setGiftType(val);
  };

  const handleSelectGiftTreatment = (val) => {
    if (!val) {
      setGiftTreatment(null);
      return;
    }
    const [category, groupIndex, specialityId, treatment, value] = val.split('_');
    setGiftTreatment({
      groupIndex,
      specialityId,
      value,
    });
    setGiftTreatmentInvalid(false);
    setGiftUpgradeInvalid(false);
    if (!giftUpgrade) return;
    const { option_id } = giftUpgrade;
    const giftUpgradeSpecialityId = option_id.split('-')[0];
    if (giftUpgradeSpecialityId !== specialityId) {
      setGiftUpgrade(undefined);
    }
  };

  const handleSelectGiftAmount = (val) => {
    setGiftAmountInvalid(false);
    setGiftAmount(val);
  };

  const handleGiftUpgrade = (val) => {
    if (giftUpgradeInvalid) setGiftUpgradeInvalid(false);
    setGiftUpgrade(val);
  };

  const getCurrencySymbol = () => {
    productsV2.load({ type: '*', city: selectedCity }).then((res) => {
      const {textSymbol} = res.currency;
      setCurrencySymbol(textSymbol);
    });
  };

  const clearDefaultTreatment = () => {
    setDefaultTreatment(null);
  };

  const handleRecipientChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...recipient, [name]: value };
    setRecipient(newData);
  };

  const handleShowFooter = (val) => {
    setShowFooter(val);
  };

  const formatedTotalPrice = useMemo(() => {
    if (voucherAmount && giftUpgrade) {
      return `${currencySymbol}${parseFloat(+voucherAmount + +giftUpgrade.upgrade_value + +deliveryOption.price).toFixed(2)}`;
    }
    if (voucherAmount) {
      return `${currencySymbol}${parseFloat(+voucherAmount + +deliveryOption.price).toFixed(2)}`;
    }
    return '–';
  }, [voucherAmount, deliveryOption, giftUpgrade]);

  const formatedAmount = useMemo(() => {
    if (voucherAmount) {
      return `${currencySymbol}${parseFloat(voucherAmount).toFixed(2)}`;
    }
    return '–';
  }, [voucherAmount]);

  const formatedDeliveryPrice = useMemo(() => {
    if (deliveryOption.price === 0) {
      return i18n._x('Free', 'Gift Card delivery');
    }
    return `+ ${currencySymbol}${parseFloat(deliveryOption.price).toFixed(2)}`;
  }, [deliveryOption]);

  const formatedUpgradePrice = useMemo(() => {
    if (giftUpgrade) {
      return `${currencySymbol}${parseFloat(giftUpgrade.upgrade_value).toFixed(2)}`;
    }
    return '–';
  }, [giftUpgrade]);

  const totalAmount = useMemo(() => {
    if (voucherAmount && giftUpgrade) {
      return parseFloat(+voucherAmount + +giftUpgrade.upgrade_value + +deliveryOption.price).toFixed(2);
    }
    if (voucherAmount) {
      return parseFloat(+voucherAmount + +deliveryOption.price).toFixed(2);
    }
    return 0;
  }, [voucherAmount, deliveryOption, giftUpgrade]);

  useEffect(() => {
    if (session && !session.user && currentStep === 3) {
      setShowFooter(false);
    } else {
      setShowFooter(true);
    }
  }, [session, currentStep]);

  useEffect(() => {
    if (selectedCity) {
      getCurrencySymbol();
    }
  }, [selectedCity]);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    sessionStorage.setItem('giftsV2', JSON.stringify({
      selectedCity,
      giftType,
      giftTreatment,
      giftAmount,
      customAmount,
      deliveryOption,
      giftMessage,
      currencySymbol,
      recipient,
      formatedTotalPrice,
      currentStep,
      giftUpgrade,
    }));
  }, [selectedCity, giftType, giftTreatment, giftAmount, customAmount, deliveryOption, giftMessage, currencySymbol, recipient, formatedTotalPrice, currentStep, giftUpgrade]);

  useEffect(() => {
    loadFromParams();
  }, []);

  return (
    <div>
      <DocumentTitle title={i18n._x('Wellness Gift Vouchers', 'Gifts page meta title')} />
      <DocumentMeta
        title={i18n._x('Wellness Gift Vouchers - Urban', 'Gifts page meta title')}
        description={i18n._x('Buy a wellness gift voucher for a friend or loved one and let them pick from a range of treatments. A practitioner could be with them in an hour.', 'Gifts page Meta Description')}
        hrefLangs={['en-gb', 'fr-fr']}
      />
      <div className="gifts__wrapper">
        <GiftsNavigation currentStep={currentStep} goBack={setCurrentStep} />
        <div>
          <div className="gifts__page-grid-wrapper">
            <div className="gifts__page-col-1">
              {currentStep === 1 && (
                <form id="voucherFormStep1" onSubmit={handleSubmit}>
                  <GiftCardBuyStepOne
                    clearDefaultTreatment={clearDefaultTreatment}
                    currencySymbol={currencySymbol}
                    currentStep={currentStep}
                    customAmount={customAmount}
                    defaultTreatment={defaultTreatment}
                    deliveryOption={deliveryOption}
                    giftAmount={giftAmount}
                    giftAmountCustomInvalid={giftAmountCustomInvalid}
                    giftAmountInvalid={giftAmountInvalid}
                    giftMessage={giftMessage}
                    giftTreatment={giftTreatment}
                    giftTreatmentInvalid={giftTreatmentInvalid}
                    giftType={giftType}
                    handleSelectCity={handleSelectCity}
                    handleSelectDeliveryOption={handleSelectDeliveryOption}
                    selectedCity={selectedCity}
                    setCustomAmount={handleSetCustomAmount}
                    setGiftAmount={handleSelectGiftAmount}
                    setGiftMessage={setGiftMessage}
                    setGiftTreatment={handleSelectGiftTreatment}
                    setGiftType={handleSelectGiftType}
                    handleGiftUpgrade={handleGiftUpgrade}
                    giftUpgrade={giftUpgrade}
                    giftUpgradeInvalid={giftUpgradeInvalid}
                    setHasGiftUpgradeOptions={setHasGiftUpgradeOptions}
                    hasGiftUpgradeOptions={hasGiftUpgradeOptions}
                  />
                </form>
              )}
              {currentStep === 2 && (
                <form id="voucherFormStep2" onSubmit={handleSubmit}>
                  <GiftCardBuyStepTwo
                    currentStep={currentStep}
                    deliveryOption={deliveryOption.value}
                    handleRecipientChange={handleRecipientChange}
                    recipient={recipient}
                    sendDateInvalid={sendDateInvalid}
                    setSendDateInvalid={setSendDateInvalid}
                  />
                </form>
              )}
              {currentStep === 3 && (
                <GiftCardBuyStepThree
                  giftUpgrade={giftUpgrade}
                  currentStep={currentStep}
                  deliveryOption={deliveryOption.value}
                  deliveryOptionLabel={deliveryOption.label}
                  edit={setCurrentStep}
                  formatedAmount={formatedTotalPrice}
                  giftMessage={giftMessage}
                  giftType={giftType}
                  recipient={recipient}
                  selectedCity={selectedCity}
                  setShowFooter={handleShowFooter}
                  voucherValue={totalAmount}

                />
              )}
            </div>
            <div className="gifts__page-col-2">
              <GiftCard
                deliveryOption={deliveryOption.value}
                deliveryPrice={formatedDeliveryPrice}
                giftMessage={giftMessage}
                giftType={giftType}
                recipient={recipient}
                showSummary
                sticky
                totalPrice={formatedTotalPrice}
                voucherPrice={formatedAmount}
                giftUpgrade={giftUpgrade}
                formatedUpgradePrice={formatedUpgradePrice}
              />
            </div>
          </div>
          <Divider />
          <GiftsFooter
            currentStep={currentStep}
            giftAmountCustomInvalid={giftAmountCustomInvalid}
            giftAmountInvalid={giftAmountInvalid}
            giftTreatmentInvalid={giftTreatmentInvalid}
            giftUpgradeInvalid={giftUpgradeInvalid}
            // paymentMethodSelected={billing.paymentInfo.paymentToken}
            showFooter={showFooter}
            totalPrice={formatedTotalPrice}
          />
        </div>
      </div>
    </div>
  );
};

GiftCardBuy.propTypes = {
  billing: PropTypes.shape({
    paymentInfo: PropTypes.shape({
      paymentToken: PropTypes.string,
    }).isRequired,
  }).isRequired,
  productsV2: PropTypes.shape({
    load: PropTypes.func,
  }).isRequired,
  session: PropTypes.object.isRequired,
  tracker: PropTypes.shape({
    track: PropTypes.func.isRequired,
  }),
};

export default inject(({
  store: {
    billing,
    productsV2,
    session,
    tracker,
  },
}) => ({
  billing,
  productsV2,
  session,
  tracker,
}))(withI18n(GiftCardBuy));



// WEBPACK FOOTER //
// ./spa/Pages/Gifts/GiftCardBuy.jsx